import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Button, TextField, MenuItem, Select, CircularProgress } from "@mui/material";
import HorizontalBarOne from "./Charts/HorizontalBarOne";
import DataTable from "react-data-table-component";
import { useNavigate, useLocation } from "react-router-dom";
import config from "../../../config";

export default function DataHealthviewdetails() {

  const navigate = useNavigate();
  const location = useLocation();
  const selectedDateRange = location.state?.selectedDateRange;
  const [syncData, setSyncData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSyncData();
  }, [selectedDateRange]);

  const fetchSyncData = async () => {
    try {
      setLoading(true);
      const baseUrl = config.API_BASE_URL_V1;
      let url = `${baseUrl}/analytics/data-health/product-sync`;

      // Add date filters if available
      if (selectedDateRange?.start_date && selectedDateRange?.end_date) {
        url += `?start_date=${selectedDateRange.start_date}&end_date=${selectedDateRange.end_date}`;
      }

      const response = await fetch(url, { headers: config.getHeaders() });
      const result = await response.json();
      setSyncData(result.data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching sync data:", err);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Chart Data ----------
  const labels = syncData.map(item => item.organization_name);
  const syncCounts = syncData.map(item => item.sync_count);

  // ---------- Table + Status Logic ----------
  const classifyStatus = (pctSuccess) => {
    if (pctSuccess == null) return { label: "Unknown", style: { bg: "#E5E7EB", border: "#9CA3AF", text: "#374151" } };
    if (pctSuccess < 50) return { label: "Critical", style: { bg: "#FFE6E6", border: "#EF4444", text: "#B91C1C" } };
    if (pctSuccess < 80) return { label: "Slow", style: { bg: "#FFF4CC", border: "#F59E0B", text: "#B45309" } };
    return { label: "Healthy", style: { bg: "#E8F9F0", border: "#10B981", text: "#065F46" } };
  };

  const tableData = useMemo(() => {
    return syncData.map(item => {
      const status = classifyStatus(item.pct_success);
      return { ...item, status };
    });
  }, [syncData]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const filteredTable = useMemo(() => {
    return tableData.filter(row =>
      row.organization_name.toLowerCase().includes(search.toLowerCase()) &&
      (statusFilter === "All" || row.status.label === statusFilter)
    );
  }, [tableData, search, statusFilter]);

  const [page, setPage] = useState(1);
  const rowsPerPage = 5;
  const totalPages = Math.ceil(filteredTable.length / rowsPerPage);
  const paginatedData = filteredTable.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const exportCSV = () => {
    if (filteredTable.length === 0) return;
    const rows = filteredTable.map(row => ({
      Client: row.organization_name,
      "Sync Count": row.sync_count,
      "Success Rate (%)": row.pct_success,
      "Avg Duration (sec)": row.avg_sync_duration_sec,
      Status: row.status.label
    }));

    const file = [
      Object.keys(rows[0]).join(","),
      ...rows.map(r => Object.values(r).join(","))
    ].join("\n");

    const blob = new Blob([file], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "DataHealth_Table.csv";
    link.click();
  };

  const columns = [
    { name: "Client", selector: (row) => row.organization_name, sortable: true },
    { name: "Sync Count", selector: (row) => row.sync_count, sortable: true },
    { name: "Success Rate (%)", selector: (row) => row.pct_success, sortable: true },
    { name: "Avg Duration (sec)", selector: (row) => row.avg_sync_duration_sec, sortable: true },
    {
      name: "Status",
      cell: (row) => (
        <span
          style={{
            background: row.status.style.bg,
            color: row.status.style.text,
            border: `1.5px solid ${row.status.style.border}`,
            padding: "6px 14px",
            borderRadius: "18px",
            fontWeight: 600,
            fontSize: "13px",
          }}
        >
          {row.status.label}
        </span>
      ),
    },
  ];


  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ color: "red" }}>
          Error loading sync data: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>

      {/* 🔙 Back button outside top-left */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{
            mb: 2,
            px: 2.5,
            py: 1,
            fontWeight: 600,
            borderRadius: "8px",
            textTransform: "none",
            borderColor: "#6A5BFF",
            color: "#6A5BFF",
            "&:hover": { background: "#6A5BFF", color: "#fff" },
          }}
        >
          Back
        </Button>
      </Box>

      {/* ------------ CHART SECTION ------------- */}
      <Box
        sx={{
          p: 3,
          background: "#fff",
          borderRadius: "10px",
          mb: 4,
          border: "1px solid #E5E7EB",
        }}
      >
        <Typography sx={{ fontWeight: 600, mb: 2 }}>
          Sync Volume Overview
        </Typography>

        <HorizontalBarOne labels={labels} latency={syncCounts} title="Sync Count" />
      </Box>


      {/* ------------ TABLE SECTION ------------- */}
      <Box sx={{ p: 3, borderRadius: "10px", border: "1px solid #E5E7EB", background: "#fff" }}>

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField size="small" label="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ width: "140px" }}>
              <MenuItem value="All">All Status</MenuItem>
              <MenuItem value="Healthy">Healthy</MenuItem>
              <MenuItem value="Slow">Slow</MenuItem>
              <MenuItem value="Critical">Critical</MenuItem>
            </Select>
          </Box>

          <Button variant="contained" onClick={exportCSV}>Export CSV</Button>
        </Box>

        <DataTable columns={columns} data={paginatedData} highlightOnHover />

        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, gap: 2 }}>
          <Button disabled={page === 1} onClick={() => setPage(page - 1)}>⬅ Prev</Button>
          <Typography>Page {page} / {totalPages}</Typography>
          <Button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next ➡</Button>
        </Box>

      </Box>
    </Box>
  );
}
